import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, SITE_URL } from '../../../hooks/useSEO';

const ThirdPartySupplyChainPage = () => {
  useSEO({
    title: 'Third-Party & Supply Chain Security for Mortgage Firms | Mortgage Moat',
    description: 'Manage vendor risks and supply chain threats targeting mortgage companies. Learn how to assess, monitor, and secure third-party relationships to meet GLBA and FFIEC requirements.',
    keywords: 'third-party risk management, vendor security, supply chain attacks, mortgage vendor compliance, GLBA vendor oversight',
    canonical: '/knowledge-center/third-party-supply-chain',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Third-Party & Supply Chain Security for Mortgage Firms',
        description: 'Managing vendor risks, securing your supply chain, and protecting against threats that originate from trusted business partners and service providers.',
        url: `${SITE_URL}/knowledge-center/third-party-supply-chain`,
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        author: { '@type': 'Organization', name: 'Mortgage Moat' },
        dateModified: '2025-07-14',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Knowledge Center', item: `${SITE_URL}/knowledge-center/third-party-supply-chain` },
          { '@type': 'ListItem', position: 3, name: 'Third-Party & Supply Chain', item: `${SITE_URL}/knowledge-center/third-party-supply-chain` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-purple-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-links-line"></i>
                Security Knowledge Center
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Third-Party & <span className="text-purple-600">Supply Chain Security</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Managing vendor risks, securing your supply chain, and protecting against threats that originate from trusted business partners and service providers
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-shield-check-line mr-2"></i>
                  Secure Your Vendors
                </Button>
                <Button variant="outline" size="lg" className="whitespace-nowrap">
                  <i className="ri-file-list-3-line mr-2"></i>
                  Vendor Risk Assessment
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-purple-600 mb-2">62%</div>
                <p className="text-gray-700 font-semibold">Breaches Involve Third Parties</p>
                <p className="text-sm text-gray-600 mt-2">Ponemon Institute Study</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-purple-600 mb-2">$7.5M</div>
                <p className="text-gray-700 font-semibold">Average Supply Chain Breach Cost</p>
                <p className="text-sm text-gray-600 mt-2">Including remediation & fines</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-purple-600 mb-2">98%</div>
                <p className="text-gray-700 font-semibold">Organizations Use Third-Party Vendors</p>
                <p className="text-sm text-gray-600 mt-2">With access to sensitive data</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-purple-50 border-l-4 border-purple-600 p-6">
                <div className="flex items-start gap-4">
                  <i className="ri-alert-line text-3xl text-purple-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your Security Is Only as Strong as Your Weakest Vendor</h3>
                    <p className="text-gray-700">
                      Financial services firms rely on dozens of vendors—from loan origination systems to credit reporting agencies. Each vendor connection represents a potential entry point for attackers. <strong>Third-party risk management</strong> is no longer optional; it's a regulatory requirement and business necessity.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Threats */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Third-Party Security Threats</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding how vendors and supply chain partners can become security vulnerabilities
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-door-open-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Vendor Access Exploitation</h3>
                <p className="text-gray-700 mb-4">
                  Attackers compromise vendor systems to gain indirect access to your network and data through trusted connections.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Compromised credentials:</strong> Stolen vendor login information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Lateral movement:</strong> Using vendor access to reach your systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Privileged access abuse:</strong> Excessive vendor permissions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Remote access vulnerabilities:</strong> Insecure vendor connections</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-600">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-code-box-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Software Supply Chain Attacks</h3>
                <p className="text-gray-700 mb-4">
                  Malicious code injected into software updates, libraries, or applications from trusted vendors.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Compromised updates:</strong> Malware in legitimate software patches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Dependency attacks:</strong> Malicious third-party libraries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Build system compromise:</strong> Infected development pipelines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Backdoor insertion:</strong> Hidden access points in vendor code</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-orange-600">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-database-2-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Data Exposure Through Vendors</h3>
                <p className="text-gray-700 mb-4">
                  Sensitive customer and business data compromised due to inadequate vendor security practices.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Vendor data breaches:</strong> Third-party systems compromised</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Insecure data handling:</strong> Poor vendor security practices</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Unauthorized data sharing:</strong> Vendors misusing your data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Cloud misconfigurations:</strong> Exposed vendor storage systems</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-600">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-service-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Service Provider Failures</h3>
                <p className="text-gray-700 mb-4">
                  Business disruption and data loss resulting from vendor outages, bankruptcies, or service failures.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Service outages:</strong> Critical vendor systems going offline</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Vendor bankruptcy:</strong> Loss of access to essential services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Data recovery issues:</strong> Inability to retrieve your data</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700"><strong>Cascading failures:</strong> One vendor affecting multiple services</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Vendor Risk Management Framework */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Vendor Risk Management</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A structured approach to identifying, assessing, and mitigating third-party risks
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl p-8 border-l-4 border-purple-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">1</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Vendor Inventory & Classification</h3>
                      <p className="text-gray-700 mb-4">
                        Maintain a complete inventory of all third-party vendors with access to your systems or data, categorized by risk level.
                      </p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm">Critical Vendors</h4>
                          <p className="text-xs text-gray-600">Core systems, customer data access, financial processing</p>
                        </div>
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm">High-Risk Vendors</h4>
                          <p className="text-xs text-gray-600">Sensitive data handling, network access, compliance impact</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl p-8 border-l-4 border-purple-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">2</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Due Diligence & Security Assessment</h3>
                      <p className="text-gray-700 mb-4">
                        Evaluate vendor security posture before engagement and regularly throughout the relationship.
                      </p>
                      <ul className="grid md:grid-cols-2 gap-3 text-sm">
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Security questionnaires (SIG, CAIQ)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">SOC 2 Type II audit reports</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">ISO 27001 certifications</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">Penetration testing results</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl p-8 border-l-4 border-purple-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">3</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Contractual Security Requirements</h3>
                      <p className="text-gray-700 mb-4">
                        Establish clear security obligations, liability terms, and incident response requirements in vendor contracts.
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-start gap-2">
                            <i className="ri-file-text-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                            <span>Data protection and encryption requirements</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-file-text-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                            <span>Breach notification timelines (24-48 hours)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-file-text-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                            <span>Right to audit vendor security controls</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <i className="ri-file-text-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                            <span>Data deletion upon contract termination</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl p-8 border-l-4 border-purple-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">4</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Monitoring & Reassessment</h3>
                      <p className="text-gray-700 mb-4">
                        Continuously monitor vendor security posture and reassess risks based on changes in the relationship or threat landscape.
                      </p>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 text-center">
                          <i className="ri-calendar-check-line text-2xl text-purple-600 mb-2"></i>
                          <p className="text-xs font-semibold text-gray-900">Annual Reviews</p>
                          <p className="text-xs text-gray-600 mt-1">Full reassessment</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <i className="ri-radar-line text-2xl text-purple-600 mb-2"></i>
                          <p className="text-xs font-semibold text-gray-900">Threat Intelligence</p>
                          <p className="text-xs text-gray-600 mt-1">Monitor vendor breaches</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 text-center">
                          <i className="ri-line-chart-line text-2xl text-purple-600 mb-2"></i>
                          <p className="text-xs font-semibold text-gray-900">Performance Metrics</p>
                          <p className="text-xs text-gray-600 mt-1">Track security KPIs</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-white rounded-xl p-8 border-l-4 border-purple-600">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">5</div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">Incident Response & Remediation</h3>
                      <p className="text-gray-700 mb-4">
                        Establish clear procedures for responding to vendor security incidents and ensuring timely remediation.
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-700 mb-3"><strong>Response Plan Includes:</strong></p>
                        <div className="grid md:grid-cols-2 gap-3 text-xs text-gray-600">
                          <div className="flex items-start gap-2">
                            <i className="ri-alert-line text-red-600 flex-shrink-0 mt-0.5"></i>
                            <span>Immediate vendor notification requirements</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <i className="ri-shield-cross-line text-red-600 flex-shrink-0 mt-0.5"></i>
                            <span>Access suspension procedures</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <i className="ri-search-eye-line text-red-600 flex-shrink-0 mt-0.5"></i>
                            <span>Impact assessment protocols</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <i className="ri-file-damage-line text-red-600 flex-shrink-0 mt-0.5"></i>
                            <span>Regulatory reporting obligations</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Vendor Security Best Practices</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Practical controls to minimize third-party risks
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Least Privilege Access</h4>
                <p className="text-sm text-gray-700">Grant vendors only the minimum access necessary to perform their services</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-user-search-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Multi-Factor Authentication</h4>
                <p className="text-sm text-gray-700">Require MFA for all vendor access to your systems and data</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Activity Monitoring</h4>
                <p className="text-sm text-gray-700">Log and review all vendor activities within your environment</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-git-branch-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Network Segmentation</h4>
                <p className="text-sm text-gray-700">Isolate vendor access from critical systems and sensitive data</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-lock-2-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Data Encryption</h4>
                <p className="text-sm text-gray-700">Encrypt all data shared with or accessible by third-party vendors</p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-file-shield-2-line text-2xl text-purple-600"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Regular Audits</h4>
                <p className="text-sm text-gray-700">Conduct periodic security audits of high-risk vendor relationships</p>
              </div>
            </div>
          </div>
        </section>

        {/* Regulatory Requirements */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Regulatory Compliance Requirements</h3>
                <p className="text-purple-100 mb-6">
                  Financial services firms must comply with strict vendor management regulations:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-3 flex items-center gap-2">
                      <i className="ri-government-line"></i>
                      Federal Requirements
                    </h4>
                    <ul className="space-y-2 text-sm text-purple-100">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>GLBA Safeguards Rule vendor oversight</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>FFIEC guidance on third-party relationships</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>OCC Bulletin 2013-29 requirements</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-3 flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      State & Industry Standards
                    </h4>
                    <ul className="space-y-2 text-sm text-purple-100">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>NYDFS Cybersecurity Regulation Part 500</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>State data breach notification laws</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>PCI DSS for payment processors</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Take Control of Your Vendor Risk
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Our vendor risk management solutions help you identify, assess, and mitigate third-party security risks while maintaining regulatory compliance. Protect your organization from supply chain vulnerabilities.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" size="lg" className="bg-white text-purple-600 hover:bg-gray-100 border-white whitespace-nowrap">
                <i className="ri-file-list-3-line mr-2"></i>
                Vendor Security Assessment
              </Button>
              <Button variant="primary" size="lg" className="bg-white text-purple-600 hover:bg-gray-100 whitespace-nowrap">
                <i className="ri-customer-service-2-line mr-2"></i>
                Speak with Expert
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ThirdPartySupplyChainPage;